import { useState, useRef, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { usePermissions } from "@/context/PermissionContext";
import { uploadLogo, updateSettings, exportBackup, exportPdfsBackup, importBackup, getSystemBackups, restoreSystemBackup, deleteSystemBackup, createSystemBackupNow, getBackupStatus, getAIPSettings, updateAIPSettings, getTotpStatus, totpEnrollStart, totpEnrollVerify, totpDisable } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Upload, Image, Loader2, Save, Plane, Trash2, KeyRound, Download, Database, AlertTriangle, Clock, RefreshCw, Shield, Calendar, HardDrive, FileText, Volume2, Play, CheckCircle2, Circle, ShieldCheck, QrCode, Copy } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";

export default function SettingsPage() {
  const { settings, updateSettings: updateContextSettings, user } = useAuth();
  const { hasPermission, isAdmin, isMasterAdmin, isExposition } = usePermissions();
  // In Exposition (Demo) mode, backup controls stay visible but clicking them
  // shows the global "Demo Mode — Visual Only" popup instead of performing the action.
  const demoBlock = () => window.dispatchEvent(new CustomEvent("demo-mode-block"));
  const guard = (fn) => (isExposition ? demoBlock : fn);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyName, setCompanyName] = useState(settings.company_name || "");
  const [previewLogo, setPreviewLogo] = useState(settings.logo_base64);
  const fileInputRef = useRef(null);
  const backupInputRef = useRef(null);
  
  // Password change state
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  // Two-factor (TOTP) state
  const [totpEnabled, setTotpEnabled] = useState(false);
  const [totpBackupRemaining, setTotpBackupRemaining] = useState(0);
  const [totpSetup, setTotpSetup] = useState(null); // { qr_code_data_url, secret }
  const [totpCode, setTotpCode] = useState("");
  const [totpBackupCodes, setTotpBackupCodes] = useState(null);
  const [totpDisablePw, setTotpDisablePw] = useState("");
  const [totpBusy, setTotpBusy] = useState(false);
  const [showDisable2fa, setShowDisable2fa] = useState(false);

  useEffect(() => {
    getTotpStatus()
      .then((r) => { setTotpEnabled(r.data.enabled); setTotpBackupRemaining(r.data.backup_codes_remaining || 0); })
      .catch(() => {});
  }, []);

  const handleTotpStart = async () => {
    setTotpBusy(true);
    try {
      const r = await totpEnrollStart();
      setTotpSetup(r.data);
      setTotpBackupCodes(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start 2FA setup");
    } finally { setTotpBusy(false); }
  };

  const handleTotpVerify = async () => {
    if (!totpCode || totpCode.length !== 6) { toast.error("Enter the 6-digit code from your authenticator app"); return; }
    setTotpBusy(true);
    try {
      const r = await totpEnrollVerify(totpCode);
      setTotpEnabled(true);
      setTotpBackupCodes(r.data.backup_codes || []);
      setTotpBackupRemaining((r.data.backup_codes || []).length);
      setTotpSetup(null);
      setTotpCode("");
      toast.success("Two-factor authentication enabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invalid code");
    } finally { setTotpBusy(false); }
  };

  const handleTotpDisable = async () => {
    if (!totpDisablePw) { toast.error("Enter your current password to disable 2FA"); return; }
    setTotpBusy(true);
    try {
      await totpDisable(totpDisablePw);
      setTotpEnabled(false);
      setTotpBackupRemaining(0);
      setTotpDisablePw("");
      setShowDisable2fa(false);
      setTotpBackupCodes(null);
      toast.success("Two-factor authentication disabled");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not disable 2FA");
    } finally { setTotpBusy(false); }
  };
  
  // Backup state
  const [exporting, setExporting] = useState(false);
  const [exportingPdfs, setExportingPdfs] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [pendingBackupFile, setPendingBackupFile] = useState(null);
  
  // Automatic Backup System state (Master Admin only)
  const [systemBackups, setSystemBackups] = useState([]);
  const [backupStatus, setBackupStatus] = useState(null);
  const [loadingBackups, setLoadingBackups] = useState(false);
  const [creatingBackup, setCreatingBackup] = useState(false);
  const [restoringBackup, setRestoringBackup] = useState(null);
  const [restoreDialogOpen, setRestoreDialogOpen] = useState(false);
  const [selectedBackupToRestore, setSelectedBackupToRestore] = useState(null);
  const [deletingBackup, setDeletingBackup] = useState(null);
  
  // AIP Configuration state
  const [aipUrl, setAipUrl] = useState("");
  const [savingAip, setSavingAip] = useState(false);

  // Landing announcement voice (per-browser preference)
  const [voices, setVoices] = useState([]);
  const [selectedVoiceName, setSelectedVoiceName] = useState(
    () => localStorage.getItem("announcement_voice_name") || ""
  );
  const [voiceEnabled, setVoiceEnabled] = useState(
    () => localStorage.getItem("announcement_voice_enabled") !== "false"
  );

  useEffect(() => {
    if (!("speechSynthesis" in window)) return;
    const loadVoices = () => {
      const list = window.speechSynthesis.getVoices() || [];
      // English voices first, then the rest — alphabetical within each group
      const sorted = [...list].sort((a, b) => {
        const aEn = /^en/i.test(a.lang) ? 0 : 1;
        const bEn = /^en/i.test(b.lang) ? 0 : 1;
        if (aEn !== bEn) return aEn - bEn;
        return a.name.localeCompare(b.name);
      });
      setVoices(sorted);
    };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => {
      window.speechSynthesis.onvoiceschanged = null;
    };
  }, []);

  const handleVoiceChange = (name) => {
    setSelectedVoiceName(name);
    localStorage.setItem("announcement_voice_name", name);
    toast.success("Announcement voice saved for this browser");
  };

  const handleVoiceEnabledChange = (checked) => {
    const val = checked === true;
    setVoiceEnabled(val);
    localStorage.setItem("announcement_voice_enabled", val ? "true" : "false");
    toast.success(val ? "Voice announcements enabled" : "Voice announcements muted");
  };

  const handleTestVoice = () => {
    if (!("speechSynthesis" in window)) {
      toast.error("Your browser does not support voice announcements");
      return;
    }
    const utter = new SpeechSynthesisUtterance(
      "Attention. Flight Alpha Bravo Charlie 1 2 3 from Geneva is approaching for parking position P 2, estimated at 14:35."
    );
    utter.rate = 0.95;
    utter.pitch = 1.05;
    utter.lang = "en-GB";
    const v = voices.find((x) => x.name === selectedVoiceName);
    if (v) utter.voice = v;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
    toast.info("Playing sample announcement...");
  };

  // Permission check
  const canEdit = hasPermission("settings", "edit");
  
  // Load AIP settings
  useEffect(() => {
    if (canEdit) {
      loadAipSettings();
    }
  }, [canEdit]);
  
  const loadAipSettings = async () => {
    try {
      const response = await getAIPSettings();
      setAipUrl(response.data.aip_url || "");
    } catch (error) {
      console.error("Failed to load AIP settings:", error);
    }
  };
  
  const handleSaveAipUrl = async () => {
    if (!aipUrl.trim()) {
      toast.error("AIP URL is required");
      return;
    }
    
    setSavingAip(true);
    try {
      await updateAIPSettings(aipUrl);
      toast.success("AIP URL saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save AIP URL");
    } finally {
      setSavingAip(false);
    }
  };
  
  // Load system backups for Master Admin (and Exposition/demo users for read-only viewing)
  useEffect(() => {
    if (isMasterAdmin || isExposition) {
      loadSystemBackups();
    }
  }, [isMasterAdmin, isExposition]);
  
  const loadSystemBackups = async () => {
    setLoadingBackups(true);
    try {
      const [backupsRes, statusRes] = await Promise.all([
        getSystemBackups(),
        getBackupStatus()
      ]);
      setSystemBackups(backupsRes.data.backups || []);
      setBackupStatus(statusRes.data);
    } catch (error) {
      console.error("Failed to load system backups:", error);
    } finally {
      setLoadingBackups(false);
    }
  };
  
  const handleCreateBackupNow = async () => {
    setCreatingBackup(true);
    try {
      const response = await createSystemBackupNow();
      toast.success(response.data.message || "Backup started in background!");
      // Refresh backup list after a short delay to allow background completion
      setTimeout(() => loadSystemBackups(), 5000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create backup");
    } finally {
      setCreatingBackup(false);
    }
  };
  
  const handleRestoreBackup = async () => {
    if (!selectedBackupToRestore) return;
    
    setRestoringBackup(selectedBackupToRestore.id);
    setRestoreDialogOpen(false);
    
    try {
      const response = await restoreSystemBackup(selectedBackupToRestore.id);
      if (response.data.success) {
        toast.success(`System restored! ${response.data.total_documents_restored} documents restored from ${new Date(selectedBackupToRestore.backup_date).toLocaleString()}`);
        // Reload page after restore
        setTimeout(() => window.location.reload(), 2000);
      } else {
        toast.error(`Restore completed with errors: ${response.data.errors.join(", ")}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to restore backup");
    } finally {
      setRestoringBackup(null);
      setSelectedBackupToRestore(null);
    }
  };
  
  const handleDeleteBackup = async (backup) => {
    setDeletingBackup(backup.id);
    try {
      await deleteSystemBackup(backup.id);
      toast.success("Backup deleted");
      loadSystemBackups();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete backup");
    } finally {
      setDeletingBackup(null);
    }
  };
  
  const formatBackupDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Image size should be less than 2MB");
      return;
    }

    setUploading(true);
    try {
      const response = await uploadLogo(file);
      const newLogo = response.data.logo_base64;
      setPreviewLogo(newLogo);
      updateContextSettings({ logo_base64: newLogo });
      toast.success("Logo uploaded successfully");
    } catch (error) {
      toast.error("Failed to upload logo");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    setSaving(true);
    try {
      await updateSettings({ logo_base64: null });
      setPreviewLogo(null);
      updateContextSettings({ logo_base64: null });
      toast.success("Logo removed");
    } catch (error) {
      toast.error("Failed to remove logo");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCompanyName = async () => {
    if (!companyName.trim()) {
      toast.error("Company name is required");
      return;
    }

    setSaving(true);
    try {
      await updateSettings({ company_name: companyName });
      updateContextSettings({ company_name: companyName });
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      toast.error("Current password is required");
      return;
    }
    if (!newPassword) {
      toast.error("New password is required");
      return;
    }
    if (newPassword.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (!(/[a-zA-Z]/.test(newPassword) && /[0-9]/.test(newPassword))) {
      toast.error("New password must contain at least one letter and one number");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    setChangingPassword(true);
    try {
      await api.post("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword
      });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  // Backup functions
  const handleExportBackup = async () => {
    setExporting(true);
    try {
      const response = await exportBackup();
      const backupData = response.data;
      
      // Create and download JSON file
      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `ebci_data_backup_${new Date().toISOString().split("T")[0]}.json`;
      link.click();
      
      toast.success("Data & Settings backup exported successfully");
    } catch (error) {
      toast.error("Failed to export backup");
    } finally {
      setExporting(false);
    }
  };

  const handleExportPdfs = async () => {
    setExportingPdfs(true);
    try {
      const response = await exportPdfsBackup();
      const backupData = response.data;
      const count = backupData?.statistics?.total_pdfs ?? 0;

      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: "application/json" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `ebci_pdfs_backup_${new Date().toISOString().split("T")[0]}.json`;
      link.click();

      toast.success(`PDF backup exported successfully (${count} file${count === 1 ? "" : "s"})`);
    } catch (error) {
      toast.error("Failed to export PDF backup");
    } finally {
      setExportingPdfs(false);
    }
  };

  const handleBackupFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith(".json")) {
      toast.error("Please select a JSON backup file");
      return;
    }
    
    setPendingBackupFile(file);
    setImportDialogOpen(true);
  };

  const handleImportBackup = async () => {
    if (!pendingBackupFile) return;
    
    setImporting(true);
    setImportDialogOpen(false);
    
    try {
      const fileContent = await pendingBackupFile.text();
      const backupData = JSON.parse(fileContent);
      
      const response = await importBackup(backupData);
      
      if (response.data.success) {
        if (response.data.backup_type === "pdfs") {
          toast.success(`PDF backup imported successfully! (${response.data.pdfs_restored} of ${response.data.pdfs_in_file} PDF file(s) restored)`);
        } else {
          const imported = Object.entries(response.data.imported_collections)
            .map(([name, count]) => `${name}: ${count}`)
            .join(", ");
          toast.success(`Backup imported successfully! (${imported})`);
        }
        // Reload page to reflect changes
        setTimeout(() => window.location.reload(), 1500);
      } else {
        toast.error(`Import completed with errors: ${response.data.errors.join(", ")}`);
      }
    } catch (error) {
      toast.error("Failed to import backup - invalid file format");
    } finally {
      setImporting(false);
      setPendingBackupFile(null);
      if (backupInputRef.current) {
        backupInputRef.current.value = "";
      }
    }
  };

  return (
    <div className="animate-fade-in" data-testid="settings-page">
      {/* Sticky Header */}
      <div className="sticky-page-header">
        <div>
          <h1 className="text-3xl font-bold text-[#0f172a] dark:text-white font-['Outfit']">Settings</h1>
          <p className="text-[#64748b] dark:text-gray-400 mt-1">Customize your application</p>
        </div>
      </div>

      <div className="max-w-2xl space-y-6">
        {/* Logo Upload */}
        <Card className="border-[#e2e8f0]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a]">
              <Image size={20} />
              Company Logo
            </CardTitle>
            <CardDescription>
              Upload your company logo to display on the login page and sidebar
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start gap-6">
              {/* Logo Preview */}
              <div className="w-40 h-24 border-2 border-dashed border-[#e2e8f0] rounded-lg flex items-center justify-center bg-[#f8fafc] overflow-hidden">
                {previewLogo ? (
                  <img 
                    src={previewLogo} 
                    alt="Company Logo" 
                    className="max-w-full max-h-full object-contain"
                  />
                ) : (
                  <div className="text-center">
                    <Plane size={32} className="mx-auto text-[#64748b] mb-1" />
                    <p className="text-xs text-[#64748b]">No logo</p>
                  </div>
                )}
              </div>

              {/* Upload Actions */}
              <div className="flex-1 space-y-3">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="logo-file-input"
                />
                {canEdit && (
                  <div className="flex gap-2">
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                      data-testid="upload-logo-btn"
                    >
                      {uploading ? (
                        <>
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <Upload size={16} className="mr-2" />
                          Upload Logo
                        </>
                      )}
                    </Button>
                    {previewLogo && (
                      <Button
                        variant="outline"
                        onClick={handleRemoveLogo}
                        disabled={saving}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid="remove-logo-btn"
                      >
                        <Trash2 size={16} className="mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>
                )}
                <p className="text-xs text-[#64748b]">
                  Recommended: PNG or SVG, max 2MB, transparent background preferred
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Company Name */}
        <Card className="border-[#e2e8f0]">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-[#0f172a]">Company Information</CardTitle>
            <CardDescription>
              Update your company details
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="Your company name"
                  disabled={!canEdit}
                  data-testid="company-name-input"
                />
              </div>
              {canEdit && (
                <Button
                  onClick={handleSaveCompanyName}
                  disabled={saving}
                  className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
                  data-testid="save-settings-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* App Info */}
        <Card className="border-[#e2e8f0]">
          <CardHeader>
            <CardTitle className="font-['Outfit'] text-[#0f172a]">Application Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-[#64748b]">Application</p>
                <p className="font-medium text-[#0f172a]">Business Aviation Manager</p>
              </div>
              <div>
                <p className="text-[#64748b]">Version</p>
                <p className="font-mono text-[#0f172a]">1.0.0</p>
              </div>
              <div>
                <p className="text-[#64748b]">Airport</p>
                <p className="font-medium text-[#0f172a]">Charleroi South Brussels</p>
              </div>
              <div>
                <p className="text-[#64748b]">ICAO Code</p>
                <p className="font-mono text-[#0f172a]">EBCI</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Landing Announcement Voice - available to all users (per-browser) */}
        <Card className="border-[#e2e8f0]" data-testid="announcement-voice-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a]">
              <Volume2 size={20} />
              Landing Announcement Voice
            </CardTitle>
            <CardDescription>
              Choose and preview the voice used for the &quot;flight approaching landing&quot; announcements. This preference is saved for this browser only.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 max-w-md">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="voice-enabled"
                  checked={voiceEnabled}
                  onCheckedChange={handleVoiceEnabledChange}
                  data-testid="voice-enabled-checkbox"
                />
                <Label htmlFor="voice-enabled" className="cursor-pointer text-[#0f172a] dark:text-white">
                  Enable spoken announcements on this browser
                </Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="voice-select" className="text-[#0f172a] dark:text-white">Voice</Label>
                <Select value={selectedVoiceName} onValueChange={handleVoiceChange}>
                  <SelectTrigger id="voice-select" data-testid="voice-select-trigger" className="bg-white border-[#e2e8f0]">
                    <SelectValue placeholder={voices.length ? "Use browser default (auto female)" : "No voices available"} />
                  </SelectTrigger>
                  <SelectContent className="max-h-72">
                    {voices.map((v) => (
                      <SelectItem key={v.name} value={v.name} data-testid={`voice-option-${v.name}`}>
                        {v.name} ({v.lang}){v.default ? " — default" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#64748b]">
                  If no voice is selected, the system automatically picks an English female voice when available.
                </p>
              </div>

              <Button
                onClick={handleTestVoice}
                className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                data-testid="test-voice-btn"
              >
                <Play size={16} className="mr-2" />
                Test Voice
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* AIP Configuration - Admin Only */}
        {canEdit && (
          <Card className="border-[#e2e8f0]" data-testid="aip-settings-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a]">
                <FileText size={20} />
                AIP Configuration
              </CardTitle>
              <CardDescription>
                Configure the Aeronautical Information Publication (AIP) source URL
              </CardDescription>
            </CardHeader>
            <CardContent>
              <fieldset disabled={isExposition} className="space-y-4 min-w-0 border-0 p-0 m-0 disabled:opacity-70">
                <div className="space-y-2">
                  <Label htmlFor="aip_url">AIP Source URL</Label>
                  <Input
                    id="aip_url"
                    value={aipUrl}
                    onChange={(e) => setAipUrl(e.target.value)}
                    placeholder="https://ops.skeyes.be/html/belgocontrol_static/eaip/..."
                    className="font-mono text-sm"
                    data-testid="aip-url-input"
                  />
                  <p className="text-xs text-[#64748b]">
                    The URL to fetch AIP data from. Changes will take effect on next refresh.
                  </p>
                </div>
                <Button
                  onClick={handleSaveAipUrl}
                  disabled={savingAip}
                  className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                  data-testid="save-aip-url-btn"
                >
                  {savingAip ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save size={16} className="mr-2" />
                      Save AIP URL
                    </>
                  )}
                </Button>
              </fieldset>
            </CardContent>
          </Card>
        )}

        {/* Change Password - Available to all users */}
        <Card className="border-[#e2e8f0]">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a]">
              <KeyRound size={20} />
              Change Password
            </CardTitle>
            <CardDescription>
              Update your account password
            </CardDescription>
          </CardHeader>
          <CardContent>
            <fieldset disabled={isExposition} className="space-y-4 max-w-md min-w-0 border-0 p-0 m-0 disabled:opacity-70">
              <div className="space-y-2">
                <Label htmlFor="current-password" className="text-[#0f172a]">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  className="bg-white border-[#e2e8f0]"
                  data-testid="current-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-password" className="text-[#0f172a]">New Password</Label>
                <Input
                  id="new-password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  className="bg-white border-[#e2e8f0]"
                  data-testid="new-password-input"
                />
              </div>

              {/* Live password requirements checklist */}
              <div className="rounded-lg border border-[#e2e8f0] bg-[#f8fafc] p-3 space-y-1.5" data-testid="password-rules">
                <p className="text-xs font-semibold text-[#475569] uppercase tracking-wide">Password requirements</p>
                {[
                  { ok: newPassword.length >= 8 && newPassword.length <= 128, label: "8–128 characters" },
                  { ok: /[a-zA-Z]/.test(newPassword), label: "At least one letter" },
                  { ok: /[0-9]/.test(newPassword), label: "At least one number" },
                  { ok: newPassword.length > 0 && newPassword === confirmPassword, label: "Passwords match" },
                ].map((rule, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm" data-testid={`password-rule-${i}`}>
                    {rule.ok ? (
                      <CheckCircle2 size={15} className="text-emerald-600 flex-shrink-0" />
                    ) : (
                      <Circle size={15} className="text-[#94a3b8] flex-shrink-0" />
                    )}
                    <span className={rule.ok ? "text-emerald-700" : "text-[#64748b]"}>{rule.label}</span>
                  </div>
                ))}
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-[#0f172a]">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  className="bg-white border-[#e2e8f0]"
                  data-testid="confirm-password-input"
                />
              </div>
              <Button
                onClick={handleChangePassword}
                disabled={changingPassword}
                className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                data-testid="change-password-btn"
              >
                {changingPassword ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Changing...
                  </>
                ) : (
                  <>
                    <KeyRound size={16} className="mr-2" />
                    Change Password
                  </>
                )}
              </Button>
            </fieldset>
          </CardContent>
        </Card>

        {/* Two-Factor Authentication (TOTP) - Available to all users */}
        <Card className="border-[#e2e8f0]" data-testid="twofa-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a]">
              <ShieldCheck size={20} />
              Two-Factor Authentication
            </CardTitle>
            <CardDescription>
              Add an authenticator app (Google Authenticator, Authy, etc.) as a second factor. Once enabled, you can reset your password and unblock your own IP using a rotating code — no email needed.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <fieldset disabled={isExposition} className="space-y-4 max-w-md min-w-0 border-0 p-0 m-0 disabled:opacity-70">
              {/* Status line */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-[#475569]">Status:</span>
                {totpEnabled ? (
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-emerald-700" data-testid="twofa-status-enabled">
                    <CheckCircle2 size={15} /> Enabled
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-[#64748b]" data-testid="twofa-status-disabled">
                    <Circle size={15} /> Disabled
                  </span>
                )}
                {totpEnabled && (
                  <span className="text-xs text-[#94a3b8]">· {totpBackupRemaining} backup code(s) left</span>
                )}
              </div>

              {/* Enable flow */}
              {!totpEnabled && !totpSetup && (
                <Button onClick={handleTotpStart} disabled={totpBusy} className="bg-[#0f172a] hover:bg-[#1e293b] text-white" data-testid="twofa-setup-btn">
                  {totpBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <QrCode size={16} className="mr-2" />}
                  Set up authenticator app
                </Button>
              )}

              {!totpEnabled && totpSetup && (
                <div className="space-y-3" data-testid="twofa-setup-panel">
                  <p className="text-sm text-[#475569]">1. Scan this QR code in your authenticator app:</p>
                  <img src={totpSetup.qr_code_data_url} alt="2FA QR code" className="w-44 h-44 rounded-lg border border-[#e2e8f0] bg-white p-2" data-testid="twofa-qr" />
                  <p className="text-xs text-[#64748b]">Can't scan? Enter this key manually:</p>
                  <code className="block text-xs bg-[#f1f5f9] rounded px-2 py-1 break-all font-mono" data-testid="twofa-secret">{totpSetup.secret}</code>
                  <p className="text-sm text-[#475569]">2. Enter the current 6-digit code:</p>
                  <Input
                    inputMode="numeric"
                    maxLength={6}
                    value={totpCode}
                    onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    placeholder="123456"
                    className="bg-white border-[#e2e8f0] tracking-[0.4em] text-center max-w-[160px]"
                    data-testid="twofa-verify-input"
                  />
                  <div className="flex gap-2">
                    <Button onClick={handleTotpVerify} disabled={totpBusy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="twofa-verify-btn">
                      {totpBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 size={16} className="mr-2" />}
                      Activate
                    </Button>
                    <Button variant="outline" onClick={() => { setTotpSetup(null); setTotpCode(""); }} data-testid="twofa-cancel-btn">Cancel</Button>
                  </div>
                </div>
              )}

              {/* Backup codes shown once after activation */}
              {totpBackupCodes && (
                <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 space-y-2" data-testid="twofa-backup-codes">
                  <p className="text-sm font-semibold text-amber-800">Save these backup codes now — they are shown only once.</p>
                  <p className="text-xs text-amber-700">Each code works once if you ever lose access to your authenticator app.</p>
                  <pre className="text-sm font-mono bg-white rounded p-2 border border-amber-200 whitespace-pre-wrap">{totpBackupCodes.join("\n")}</pre>
                  <Button size="sm" variant="outline" onClick={() => { navigator.clipboard.writeText(totpBackupCodes.join("\n")); toast.success("Backup codes copied"); }} data-testid="twofa-copy-backup-btn">
                    <Copy size={14} className="mr-1" /> Copy codes
                  </Button>
                </div>
              )}

              {/* Disable flow */}
              {totpEnabled && !showDisable2fa && (
                <Button variant="outline" onClick={() => setShowDisable2fa(true)} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="twofa-disable-show-btn">
                  Disable 2FA
                </Button>
              )}
              {totpEnabled && showDisable2fa && (
                <div className="space-y-2" data-testid="twofa-disable-panel">
                  <Label className="text-[#0f172a]">Confirm your current password to disable 2FA</Label>
                  <Input
                    type="password"
                    value={totpDisablePw}
                    onChange={(e) => setTotpDisablePw(e.target.value)}
                    placeholder="Current password"
                    className="bg-white border-[#e2e8f0] max-w-xs"
                    data-testid="twofa-disable-password-input"
                  />
                  <div className="flex gap-2">
                    <Button onClick={handleTotpDisable} disabled={totpBusy} className="bg-red-600 hover:bg-red-700 text-white" data-testid="twofa-disable-confirm-btn">
                      {totpBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                      Disable
                    </Button>
                    <Button variant="outline" onClick={() => { setShowDisable2fa(false); setTotpDisablePw(""); }}>Cancel</Button>
                  </div>
                </div>
              )}
            </fieldset>
          </CardContent>
        </Card>

        {/* System Backup - Admin Only (visible to Exposition/demo, actions demo-blocked) */}
        {(isAdmin || isExposition) && (
          <Card className="border-[#e2e8f0]">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a]">
                <Database size={20} />
                System Backup
              </CardTitle>
              <CardDescription>
                Export or import your system backup. Data & Settings and PDF files are saved as two separate files.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-amber-800 dark:text-amber-300">Two-file backup</p>
                      <p className="text-sm text-amber-700 dark:text-amber-400 mt-1">
                        <strong>Data &amp; Settings</strong> includes all flights, users (encrypted passwords), aircraft/airport data, permissions and settings — this is the critical, lightweight file.
                        <strong> PDF Files</strong> exports the procedure documents separately (larger). Restore Data &amp; Settings first, then import the PDF file. Importing replaces existing data.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
                  {/* Export Data & Settings */}
                  <Button
                    onClick={guard(handleExportBackup)}
                    disabled={exporting}
                    className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                    data-testid="export-backup-btn"
                  >
                    {exporting ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Exporting...
                      </>
                    ) : (
                      <>
                        <Download size={16} className="mr-2" />
                        Export Data &amp; Settings
                      </>
                    )}
                  </Button>

                  {/* Export PDFs */}
                  <Button
                    onClick={guard(handleExportPdfs)}
                    disabled={exportingPdfs}
                    variant="outline"
                    className="border-[#e2e8f0] text-[#0f172a] dark:text-white dark:border-[#334155]"
                    data-testid="export-pdfs-btn"
                  >
                    {exportingPdfs ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Exporting PDFs...
                      </>
                    ) : (
                      <>
                        <Download size={16} className="mr-2" />
                        Export PDF Files
                      </>
                    )}
                  </Button>

                  {/* Import Backup (auto-detects Data or PDF file) */}
                  <input
                    ref={backupInputRef}
                    type="file"
                    accept=".json"
                    onChange={handleBackupFileSelect}
                    className="hidden"
                    data-testid="backup-file-input"
                  />
                  <Button
                    onClick={guard(() => backupInputRef.current?.click())}
                    disabled={importing}
                    variant="outline"
                    className="border-[#e2e8f0] text-[#0f172a] dark:text-white dark:border-[#334155]"
                    data-testid="import-backup-btn"
                  >
                    {importing ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Importing...
                      </>
                    ) : (
                      <>
                        <Upload size={16} className="mr-2" />
                        Import Backup
                      </>
                    )}
                  </Button>
                </div>

                <p className="text-xs text-[#64748b] dark:text-gray-400">
                  Files are saved as JSON with a date stamp (e.g., ebci_data_backup_2026-02-01.json and ebci_pdfs_backup_2026-02-01.json). Import auto-detects which file you selected.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Automatic Backup System - Master Admin Only (visible to Exposition/demo, actions demo-blocked) */}
        {(isMasterAdmin || isExposition) && (
          <Card className="border-[#d4af37] border-2" data-testid="auto-backup-section">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 font-['Outfit'] text-[#0f172a]">
                <Shield size={20} className="text-[#d4af37]" />
                Automatic Backup System
                <span className="ml-2 px-2 py-0.5 text-xs bg-[#d4af37] text-[#0f172a] rounded-full font-semibold">
                  MASTER ADMIN
                </span>
              </CardTitle>
              <CardDescription>
                System automatically creates backups every night at 23:59. Only you can access and restore these backups.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Backup Status */}
                {backupStatus && (
                  <div className="p-4 bg-[#f8fafc] dark:bg-[#1e293b] rounded-lg border border-[#e2e8f0] dark:border-[#334155]">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <p className="text-[#64748b] dark:text-gray-400">Status</p>
                        <p className="font-medium text-green-600 flex items-center gap-1">
                          <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                          Active
                        </p>
                      </div>
                      <div>
                        <p className="text-[#64748b] dark:text-gray-400">Next Backup</p>
                        <p className="font-medium text-[#0f172a] dark:text-white flex items-center gap-1">
                          <Clock size={14} />
                          23:59 (Daily)
                        </p>
                      </div>
                      <div>
                        <p className="text-[#64748b] dark:text-gray-400">Total Backups</p>
                        <p className="font-medium text-[#0f172a] dark:text-white">{backupStatus.total_backups} / 30</p>
                      </div>
                      <div>
                        <p className="text-[#64748b] dark:text-gray-400">Latest Backup</p>
                        <p className="font-medium text-[#0f172a] dark:text-white">
                          {backupStatus.latest_backup ? formatBackupDate(backupStatus.latest_backup.backup_date) : "None"}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={guard(handleCreateBackupNow)}
                    disabled={creatingBackup}
                    className="bg-[#d4af37] hover:bg-[#b5952f] text-[#0f172a]"
                    data-testid="create-backup-now-btn"
                  >
                    {creatingBackup ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      <>
                        <HardDrive size={16} className="mr-2" />
                        Create Backup Now
                      </>
                    )}
                  </Button>
                  <Button
                    onClick={loadSystemBackups}
                    disabled={loadingBackups}
                    variant="outline"
                    className="border-[#e2e8f0] dark:border-[#334155]"
                    data-testid="refresh-backups-btn"
                  >
                    <RefreshCw size={16} className={`mr-2 ${loadingBackups ? "animate-spin" : ""}`} />
                    Refresh
                  </Button>
                </div>

                {/* Warning */}
                <div className="p-4 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200 dark:border-red-800">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-medium text-red-800 dark:text-red-300">Restore Warning</p>
                      <p className="text-sm text-red-700 dark:text-red-400 mt-1">
                        Restoring a backup will <strong>DELETE ALL CURRENT DATA</strong> and replace it with the backup data. 
                        This action cannot be undone. Only use this in case of emergency or system crash.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Backup List */}
                <div className="space-y-2">
                  <h4 className="font-medium text-[#0f172a] dark:text-white flex items-center gap-2">
                    <Calendar size={16} />
                    Available Backups
                  </h4>
                  
                  {loadingBackups ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-[#d4af37]" />
                    </div>
                  ) : systemBackups.length === 0 ? (
                    <div className="text-center py-8 text-[#64748b] dark:text-gray-400">
                      <HardDrive size={32} className="mx-auto mb-2 opacity-50" />
                      <p>No backups available yet</p>
                      <p className="text-sm">First automatic backup will be created tonight at 23:59</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-80 overflow-y-auto pr-2">
                      {systemBackups.map((backup) => (
                        <div
                          key={backup.id}
                          className="flex items-center justify-between p-3 bg-[#f8fafc] dark:bg-[#1e293b] rounded-lg border border-[#e2e8f0] dark:border-[#334155] hover:border-[#d4af37] transition-colors"
                          data-testid={`backup-item-${backup.id}`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className={`px-2 py-0.5 text-xs rounded-full ${
                                backup.backup_type === "automatic" 
                                  ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400"
                                  : "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400"
                              }`}>
                                {backup.backup_type === "automatic" ? "Auto" : "Manual"}
                              </span>
                              <span className="font-medium text-[#0f172a] dark:text-white">
                                {formatBackupDate(backup.backup_date)}
                              </span>
                            </div>
                            <div className="text-xs text-[#64748b] dark:text-gray-400 mt-1">
                              {backup.statistics?.total_documents?.toLocaleString() || 0} documents · 
                              {backup.statistics?.total_collections || 0} collections · 
                              Created by: {backup.created_by || "System"}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              onClick={guard(() => {
                                setSelectedBackupToRestore(backup);
                                setRestoreDialogOpen(true);
                              })}
                              disabled={restoringBackup === backup.id}
                              className="bg-[#0f172a] hover:bg-[#1e293b] text-white"
                              data-testid={`restore-backup-${backup.id}`}
                            >
                              {restoringBackup === backup.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <RefreshCw size={14} className="mr-1" />
                                  Restore
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={guard(() => handleDeleteBackup(backup))}
                              disabled={deletingBackup === backup.id}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                              data-testid={`delete-backup-${backup.id}`}
                            >
                              {deletingBackup === backup.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Trash2 size={14} />
                              )}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Import Confirmation Dialog */}
      <AlertDialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
        <AlertDialogContent className="bg-white dark:bg-[#1e293b]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0f172a] dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Confirm Import
            </AlertDialogTitle>
            <AlertDialogDescription className="dark:text-gray-300">
              Are you sure you want to import this backup? This will <strong>replace ALL existing data</strong> including:
              <ul className="mt-2 ml-4 list-disc text-sm">
                <li>All flights</li>
                <li>All users and passwords</li>
                <li>All aircraft and airport data</li>
                <li>All permissions and roles</li>
                <li>All settings</li>
              </ul>
              <p className="mt-3 text-red-600 dark:text-red-400 font-medium">This action cannot be undone!</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e2e8f0] dark:border-[#334155] dark:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleImportBackup}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-import-backup-btn"
            >
              Yes, Replace All Data
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Restore Backup Confirmation Dialog */}
      <AlertDialog open={restoreDialogOpen} onOpenChange={setRestoreDialogOpen}>
        <AlertDialogContent className="bg-white dark:bg-[#1e293b]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#0f172a] dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              Restore System Backup
            </AlertDialogTitle>
            <AlertDialogDescription className="dark:text-gray-300">
              <p className="mb-3">
                You are about to restore the system from backup:
              </p>
              {selectedBackupToRestore && (
                <div className="p-3 bg-[#f8fafc] dark:bg-[#0f172a] rounded-lg mb-3">
                  <p className="font-medium text-[#0f172a] dark:text-white">
                    {formatBackupDate(selectedBackupToRestore.backup_date)}
                  </p>
                  <p className="text-sm text-[#64748b] dark:text-gray-400">
                    {selectedBackupToRestore.statistics?.total_documents?.toLocaleString() || 0} documents · 
                    {selectedBackupToRestore.backup_type === "automatic" ? "Automatic" : "Manual"} backup
                  </p>
                </div>
              )}
              <p className="text-sm">This will <strong>DELETE ALL CURRENT DATA</strong> and replace it with the backup data:</p>
              <ul className="mt-2 ml-4 list-disc text-sm">
                <li>All flights and canceled flights</li>
                <li>All users and passwords</li>
                <li>All aircraft and airport data</li>
                <li>All permissions and roles</li>
                <li>All settings and notes</li>
                <li>All login history</li>
              </ul>
              <p className="mt-3 text-red-600 dark:text-red-400 font-medium">⚠️ This action cannot be undone!</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#e2e8f0] dark:border-[#334155] dark:text-white">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRestoreBackup}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-restore-backup-btn"
            >
              Yes, Restore Backup
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
